import { Button, Popover } from '@salesforce/design-system-react';
import { useEffect, useRef, useState } from 'react';

import { PageMeta, QualityAdjustmentNavigation } from '../../../shared/components';
import { IconCategory } from '../../../shared/types/Enum';
import { ContractPricingSummarySection } from '../components/ContractPricingSummarySection';
import { QualityResultsSection } from '../components/QualityResultsSection';
import { QualitySpecsWorkbench } from '../components/QualitySpecsWorkbench';
import { QualityFormulaStoreProvider, useQualityFormulaStore } from '../store/QualityFormulaStoreProvider';

const ResetInputsButton: React.FC<{ variant?: 'brand' | 'neutral' | 'outline-brand' }> = ({ variant = 'brand' }) => {
  const handleResetInputs = useQualityFormulaStore(store => store.handleResetInputs);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // The installed Popover's own built-in outside-click detection mounts the instant the dialog
  // opens, which can treat the very click that just opened it (still bubbling up to `document`)
  // as a click outside the dialog and close it again before a single frame ever paints — see
  // AddSpecMenu's own doc comment for the same workaround. Listening for `pointerdown` (which
  // always fires before the triggering `click`) rather than `click` itself means this listener,
  // attached only after the open click's own state update has already committed, can never
  // observe that same click.
  useEffect(() => {
    if (!confirmOpen) return;
    const handleOutsidePointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target)) {
        setConfirmOpen(false);
      }
    };
    document.addEventListener('pointerdown', handleOutsidePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handleOutsidePointerDown);
    };
  }, [confirmOpen]);

  return (
    <div ref={containerRef} style={{ display: 'inline-block' }}>
      <Popover
        align="bottom right"
        body={
          <div style={{ maxWidth: 260 }}>
            <p className="slds-text-body_regular slds-p-bottom_small">
              Clear entered field values for the specs and actual test values? This can’t be undone.
            </p>
            <div className="slds-grid slds-grid_align-end" style={{ gap: 'var(--slds-g-spacing-2)' }}>
              <Button
                iconCategory={IconCategory.Utility}
                iconName="close"
                iconPosition="left"
                label="Cancel"
                onClick={() => {
                  setConfirmOpen(false);
                }}
                variant="neutral"
              />
              <Button
                label="Reset"
                onClick={() => {
                  handleResetInputs();
                  setConfirmOpen(false);
                }}
                variant="destructive"
              />
            </div>
          </div>
        }
        hasNoCloseButton
        isOpen={confirmOpen}
        onRequestClose={() => {
          setConfirmOpen(false);
        }}
        openOn="click"
      >
        <Button
          iconCategory={IconCategory.Utility}
          iconName="undo"
          iconPosition="left"
          label="Reset"
          onClick={() => {
            setConfirmOpen(current => !current);
          }}
          title="Clears entered field values and test values"
          variant={variant}
        />
      </Popover>
    </div>
  );
};

const QualityFormulasWorkspace: React.FC = () => {
  // Gates the divider preceding QualityResultsSection, which otherwise renders nothing until a
  // formula has been tested (see that component's own doc comment).
  const hasPreviewResults = useQualityFormulaStore(store => store.results.length > 0 && store.summary !== null);

  return (
    <div
      className="slds-p-around_medium"
      // Keep the page background viewport-height without stretching the workbench's cards.
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--slds-g-spacing-3)',
        margin: '0 auto',
        minHeight: '100vh',
        width: '100%',
      }}
    >
      <PageMeta
        description="Build and review quality pricing formulas in a guided, interactive playground."
        title="Quality Pricing Formula"
      />
      <QualityAdjustmentNavigation />
      <div style={{ alignItems: 'baseline', display: 'flex', flexWrap: 'wrap', gap: 'var(--slds-g-spacing-2)' }}>
        <h1 className="slds-text-heading_medium">Quality Pricing Formula</h1>
        <span className="slds-text-body_regular slds-text-color_weak">
          Build, preview, and test quality adjustment rules against the contract below — nothing here is saved.
        </span>
      </div>

      <ContractPricingSummarySection />

      <hr className="slds-m-vertical_none" />

      <QualitySpecsWorkbench extraActions={<ResetInputsButton variant="neutral" />} />

      {hasPreviewResults ? (
        <>
          <hr className="slds-m-vertical_none" />
          <QualityResultsSection />
        </>
      ) : null}
    </div>
  );
};

export const QualityFormulasPage: React.FC = () => (
  <QualityFormulaStoreProvider>
    <QualityFormulasWorkspace />
  </QualityFormulaStoreProvider>
);
